import React, {ReactElement} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import {cx} from '@emotion/css';

import {ForgotPassword} from '@fo/shared-data/apis/Identity';
import getIdentityError, {IdentityErrors, IdentityForm} from '@fo/shared-data/identity/errorMessages';
import useForgotPassword from '@fo/shared-data/identity/useForgotPassword/useForgotPassword';
import EmailAddressField from '@fo/designSystem/Fields/Email/EmailWithFormikField';
import useShowModalOnCondition from '@fo/designSystem/BaseModal/useShowModalOnCondition';
import SinglePageForm from '@fo/designSystem/SinglePageForm/SinglePageForm';
import {segmentEvents, track} from '@fo/shared-analytics/tracking/segment';

import IdentityErrorsModal from '../IdentityErrorsModal/IdentityErrorsModal';

import styles from './ForgotPasswordForm.styles';

interface Props {
  afterSubmit: () => void;
}

function ForgotPasswordForm({afterSubmit}: Props): ReactElement {
  const {formatMessage} = useIntl();
  const {submit, error: identityError, isLoading} = useForgotPassword();
  const showIdentityModal = Boolean(
    identityError &&
      (identityError === IdentityErrors.INVALID_PARAMETER || identityError === IdentityErrors.LIMIT_EXCEEDED)
  );
  useShowModalOnCondition(<IdentityErrorsModal />, showIdentityModal);

  const initialValues: ForgotPassword = {
    emailAddress: '',
  };

  const handleOnSubmit = async (formData: ForgotPassword) => {
    const submitSuccess = await submit(formData);
    if (submitSuccess) {
      afterSubmit();
    }
  };

  const isFormDisabled = false;

  return (
    <Formik initialValues={initialValues} onSubmit={handleOnSubmit} validateOnChange={false} validateOnBlur={false}>
      {({errors, handleSubmit}) => {
        let warningMessage;
        if (identityError && !showIdentityModal) {
          warningMessage = getIdentityError(identityError, formatMessage, IdentityForm.FORGOT_PASSWORD);
        }
        return (
          <SinglePageForm
            className={cx(styles.container)}
            errors={errors}
            submitButton={{
              id: 'forgotPassword',
              label: formatMessage({id: 'phoenix.forgotPassword.form.button.text'}),
              loading: isLoading,
              testId: 'button.forgotPassword.submit',
              onClick: () => track(segmentEvents.fundingCloud.account.forgotPasswordSubmit),
            }}
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            isFormDisabled={isFormDisabled}
          >
            <EmailAddressField<ForgotPassword>
              label={formatMessage({id: 'phoenix.fields.email.label'})}
              name="emailAddress"
              placeholder={formatMessage({id: 'phoenix.fields.email.placeholder'})}
              required
              warningMessage={warningMessage}
              isDisabled={isFormDisabled}
            />
          </SinglePageForm>
        );
      }}
    </Formik>
  );
}

export default ForgotPasswordForm;
